/**
 * @module engine/balance
 * @summary Carries the throughput typed on the sources through the drawing.
 * @description
 *   A process flow diagram already says what is connected to what and, since
 *   the flow work, which way each run goes and whether it carries product or
 *   air. That is nearly a mass balance already. All it is missing is a number,
 *   and the number belongs at the top: type 900 kg/h on the resin silo and
 *   450 on the chalk, and every stream below them follows.
 *
 *   Three rules, and they are the whole model:
 *
 *     • **A rate on an item is what that item FEEDS IN.** Not what passes
 *       through it. A silo discharging 900 carries 900; a side feeder adding 75
 *       to a stream already carrying 900 passes on 975. So a machine fed from
 *       upstream normally carries no rate of its own, and a source carries
 *       nothing else.
 *     • **What arrives leaves.** Steady state: a machine's output is its input
 *       plus whatever it feeds in itself. Nothing is stored and nothing is
 *       lost, which is what a flow sheet asserts.
 *     • **A split divides evenly unless it is told otherwise.** Two outgoing
 *       product streams take half each — which is right for a diverter and
 *       wrong for a cyclone, so pinning a rate on one stream lets the others
 *       share what is left. That is the only place a figure has to be typed
 *       twice, and it is the only place the drawing genuinely cannot know.
 *
 *   Only PRODUCT streams carry mass. A vent line off a cyclone and the air
 *   supply off a blower are already known to be air (see `engine/streams`), and
 *   putting a product throughput on them would be a lie in kilograms.
 *
 *   // INVARIANT: nothing here writes to the document. It reads `S` and returns
 *   a fresh answer, so it can be called from either view's draw path.
 */
import { S } from './state';
import type { Connection, Pipe3, Shape } from '../types';
import {
  directionOfConnection,
  directionOfPipe3,
  mediumOfConnection,
  mediumOfPipe3,
  shapeIndex,
} from './streams';
import {
  FG_COLOUR,
  addMix,
  addedAt,
  buildRecipes,
  colourForPart,
  fgDisplayName,
  fgKey,
  fgShapeOf,
  formulationById,
  formulationOf,
  isFgStart,
  scaleMix,
  shortMaterialName,
  type Mix,
  type Recipe,
} from './formulation';

/** What one item passes, in kg/h. */
export interface NodeBalance {
  /** Arriving down product streams. */
  in: number;
  /** Fed in by this item itself — the number somebody typed. */
  added: number;
  /** Leaving down product streams. */
  out: number;
}

export interface Balance {
  /** kg/h on each product stream, by connector or 3D-pipe id. */
  stream: Map<string, number>;
  /** What each item passes. */
  node: Map<string, NodeBalance>;
  /**
   * Product streams the balance could not put a figure on, because they sit in
   * a loop — regrind returned to the feed, most often. Left out of `stream`
   * rather than guessed at: a recycle needs an assumed rate to converge, and
   * inventing one would put a number on the sheet that nothing supports.
   */
  looped: Set<string>;
  /** True once anything has been given a rate. Nothing is drawn before that. */
  any: boolean;
  /**
   * What each product stream is MADE OF: component key → kg/h, keyed by
   * connector or 3D-pipe id. Upstream of the FG point the keys are EP part
   * numbers; downstream there is one key, the finished good. Empty for a
   * stream whose mass was never attributed to a material.
   */
  streamMix: Map<string, Mix>;
  /** The same breakdown for what LEAVES each item. */
  nodeMix: Map<string, Mix>;
  /** The sheet for every line on the drawing, authored rather than discovered. */
  recipes: Recipe[];
  /** What the finished good made at each FG point is called, by shape id. */
  fgName: Map<string, string>;
  /** Which line each FG point makes, by shape id; '' when it belongs to none. */
  fgFormulation: Map<string, string>;
  /**
   * The colour a stream flows in, by connector or 3D-pipe id. Only set where a
   * run carries ONE thing; a blend keeps the default material colour.
   */
  streamColour: Map<string, string>;
  /** True once any charge point names a material. */
  anyFeed: boolean;
  /**
   * Item ids in the order the sweep settled them — upstream before downstream.
   *
   * Kept because it is free here and expensive to recover afterwards: listing
   * the plant a finished good passes through on its way to packing means
   * walking the drawing in flow order, and the sweep has just done exactly
   * that. Items inside a recycle loop never settle, so they are not listed.
   */
  order: string[];
}

/** One product stream, resolved to the two items it runs between. */
interface Edge {
  id: string;
  from: string;
  to: string;
  /** Pinned throughput, when the stream carries one. */
  pinned?: number;
}

const rateOf = (v: number | undefined): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : undefined;

/** The product streams of the drawing, pointing the way they actually flow. */
function productEdges(byId: Map<string, Shape>): Edge[] {
  const out: Edge[] = [];
  const add = (id: string, a: string, b: string, reversed: boolean, pinned?: number) => {
    if (!byId.has(a) || !byId.has(b) || a === b) return;
    out.push(reversed ? { id, from: b, to: a, pinned } : { id, from: a, to: b, pinned });
  };

  for (const cn of S.connections as Connection[]) {
    // `hide3` is deliberately ignored: hiding a pipe in the 3D view does not
    // stop the process running through it.
    if (mediumOfConnection(cn, byId) !== 'material') continue;
    add(cn.id, cn.fromId, cn.toId, directionOfConnection(cn) === 'reverse', rateOf(cn.rate));
  }
  for (const q of S.pipes3 as Pipe3[]) {
    if (mediumOfPipe3(q, byId) !== 'material') continue;
    // A run teed into another run, or hanging on a bare point, has no second
    // machine to carry anything to. It is pipework rather than a stream.
    if (!q.from.shapeId || !q.to.shapeId) continue;
    add(q.id, q.from.shapeId, q.to.shapeId, directionOfPipe3(q) === 'reverse', rateOf(q.rate));
  }
  return out;
}

/**
 * Work the whole drawing out from the rates typed on it.
 *
 * Linear in the size of the drawing: one pass to build the graph, one
 * topological sweep down it. Cheap enough to call from a draw path.
 */
export function computeBalance(): Balance {
  const byId = shapeIndex(S.shapes);
  const edges = productEdges(byId);

  const stream = new Map<string, number>();
  const node = new Map<string, NodeBalance>();
  const looped = new Set<string>();

  const outOf = new Map<string, Edge[]>();
  const indeg = new Map<string, number>();
  for (const s of S.shapes) indeg.set(s.id, 0);
  for (const e of edges) {
    let list = outOf.get(e.from);
    if (!list) {
      list = [];
      outOf.set(e.from, list);
    }
    list.push(e);
    indeg.set(e.to, (indeg.get(e.to) ?? 0) + 1);
  }

  const fed = new Map<string, number>();
  let any = false;
  let anyFeed = false;
  for (const s of S.shapes) {
    if (rateOf(s.rate) !== undefined) any = true;
    if (s.feed?.some((f) => f?.part)) {
      anyFeed = true;
      any = true;
    }
  }

  // ── Composition, carried down the same sweep ──
  // `mix` is what a stream is made of. `prov` is the same arithmetic keyed by
  // material AND the charge point it came from, which is what lets a recipe say
  // which feeder to put the chalk in. They merge and split identically, so a
  // split divides provenance in the same proportion it divides mass.
  const streamMix = new Map<string, Mix>();
  const nodeMix = new Map<string, Mix>();
  const fedMix = new Map<string, Mix>();
  const streamProv = new Map<string, Mix>();
  const fedProv = new Map<string, Mix>();
  const streamColour = new Map<string, string>();
  const fgName = new Map<string, string>();
  const fgFormulation = new Map<string, string>();
  // The sheets are AUTHORED, not discovered: they exist whether or not anything
  // has been drawn yet, which is what lets a recipe be written first and the
  // feeders filled in against it afterwards.
  const recipes: Recipe[] = buildRecipes(S.shapes);
  const provKey = (part: string, shapeId: string) => `${part}|${shapeId}`;

  // Kahn's order. Anything still holding an in-degree at the end is inside a
  // loop, and so is everything it feeds.
  const queue: string[] = [];
  for (const s of S.shapes) if ((indeg.get(s.id) ?? 0) === 0) queue.push(s.id);

  const settled = new Set<string>();
  const order: string[] = [];
  while (queue.length) {
    const id = queue.shift()!;
    settled.add(id);
    order.push(id);
    const shape = byId.get(id);
    const arrived = fed.get(id) ?? 0;
    const charge = shape ? addedAt(shape) : { total: 0, mix: new Map() as Mix };
    const added = charge.total;
    const total = arrived + added;

    // What leaves this item is what arrived plus what it charges in.
    let mix: Mix = new Map(fedMix.get(id) ?? []);
    let prov: Mix = new Map(fedProv.get(id) ?? []);
    addMix(mix, charge.mix);
    for (const [part, kgh] of charge.mix) prov.set(provKey(part, id), kgh);

    // Compounding: the breakdown becomes a product. Everything from here to the
    // packing area carries one named component rather than a list of
    // ingredients, because that is what is being handled from here on.
    if (shape && isFgStart(shape)) {
      // Which line's product this is. Said outright when the item has been put
      // on a line; otherwise read from what arrived — the feeders that charged
      // this material belong to a line, and the one that sent the most of it is
      // the line being made here.
      let f = formulationOf(shape);
      if (!f) {
        const weight = new Map<string, number>();
        for (const [k, kgh] of prov) {
          const src = byId.get(k.slice(k.indexOf('|') + 1));
          const fid = src?.formulationId;
          if (fid) weight.set(fid, (weight.get(fid) ?? 0) + kgh);
        }
        let best = 0;
        for (const [fid, w] of weight)
          if (w > best) {
            best = w;
            f = formulationById(fid);
          }
      }
      fgFormulation.set(id, f?.id ?? '');
      fgName.set(
        id,
        fgDisplayName(f, shape.fg?.trim() || `FG @ ${shape.label || shape.tag || shape.type}`),
      );

      const made = new Map<string, number>();
      if (total > 0) made.set(fgKey(id), total);
      mix = made;
      prov = new Map(made);
    }

    nodeMix.set(id, mix);

    const outs = outOf.get(id) ?? [];
    // A pinned stream takes what it was given; the rest share what is left.
    let claimed = 0;
    for (const e of outs) if (e.pinned !== undefined) claimed += e.pinned;
    const spare = Math.max(0, total - claimed);
    const free = outs.filter((e) => e.pinned === undefined);
    const each = free.length ? spare / free.length : 0;

    let leaving = 0;
    for (const e of outs) {
      const v = e.pinned ?? each;
      stream.set(e.id, v);
      leaving += v;
      fed.set(e.to, (fed.get(e.to) ?? 0) + v);
      // Every leg of a split carries the same recipe, just less of it.
      const f = total > 0 ? v / total : 0;
      const sm = scaleMix(mix, f);
      const sp = scaleMix(prov, f);
      streamMix.set(e.id, sm);
      streamProv.set(e.id, sp);
      // A run carrying ONE thing flows in that thing's colour. A blend keeps
      // the ordinary material amber: it is the compound being made, and
      // painting it as whichever ingredient happens to be heaviest would be a
      // lie you could watch.
      if (sm.size === 1) {
        const [key] = sm.keys();
        if (fgShapeOf(key)) streamColour.set(e.id, FG_COLOUR);
        else {
          // Which line's palette, from the feeder the material came down.
          let src: Shape | undefined;
          let best = 0;
          for (const [pk, kgh] of sp)
            if (kgh > best) {
              best = kgh;
              src = byId.get(pk.slice(pk.indexOf('|') + 1));
            }
          streamColour.set(e.id, colourForPart(formulationOf(src), key));
        }
      }
      let dm = fedMix.get(e.to);
      if (!dm) fedMix.set(e.to, (dm = new Map()));
      addMix(dm, sm);
      let dp = fedProv.get(e.to);
      if (!dp) fedProv.set(e.to, (dp = new Map()));
      addMix(dp, sp);
      const d = (indeg.get(e.to) ?? 1) - 1;
      indeg.set(e.to, d);
      if (d === 0) queue.push(e.to);
    }
    node.set(id, { in: arrived, added, out: leaving });
  }

  // Everything the sweep never reached is in a loop, or fed by one.
  for (const e of edges) if (!settled.has(e.from)) looped.add(e.id);
  for (const s of S.shapes)
    if (!settled.has(s.id)) {
      const charge = addedAt(s);
      node.set(s.id, { in: fed.get(s.id) ?? 0, added: charge.total, out: 0 });
    }

  return {
    stream, node, looped, any, streamMix, nodeMix, recipes,
    fgName, fgFormulation, streamColour, anyFeed, order,
  };
}

/**
 * A throughput as it is written on a drawing.
 *
 * Always kg/h. Switching to t/h above some threshold would put two units on one
 * sheet and make two lines on the same run look like different quantities,
 * which is exactly the confusion a balance exists to remove.
 */
export function formatRate(kgh: number): string {
  if (!Number.isFinite(kgh)) return '';
  const n = kgh >= 100 ? Math.round(kgh) : Math.round(kgh * 10) / 10;
  return `${n} kg/h`;
}

/**
 * What a stream is carrying, as a name to write next to its throughput.
 *
 * Only where a name is worth more than the space it takes:
 *
 *   • one component  → that material. A line off a chalk feeder says CaCO3,
 *                      which is the whole point of naming what a feeder doses.
 *   • past the FG    → the finished good, from the extruder to the bagging
 *                      station. This is the one an operator reads.
 *   • anything else  → nothing. Between the last feeder and the extruder a
 *                      stream is five materials at once; listing them would
 *                      bury the drawing, and the recipe is a sheet, not a
 *                      pipe label.
 */
export function streamMaterial(bal: Balance, streamId: string): string {
  const mix = bal.streamMix.get(streamId);
  if (!mix || mix.size !== 1) return '';
  const [key] = mix.keys();
  const fgId = fgShapeOf(key);
  return fgId ? (bal.fgName.get(fgId) ?? '') : shortMaterialName(key);
}

/**
 * The colour a stream flows in, or '' to leave it the default.
 *
 * One place, so the 3D flow and its legend cannot disagree about which colour
 * means which material — the whole point of colouring them.
 */
export function streamColourOf(bal: Balance, streamId: string): string {
  return bal.streamColour.get(streamId) ?? '';
}

/** A stream's throughput as text, or '' when the drawing carries no figures. */
export function streamRateText(bal: Balance, streamId: string): string {
  const kgh = bal.any ? bal.stream.get(streamId) : undefined;
  return kgh ? formatRate(kgh) : '';
}

/**
 * A stream's full caption on ONE line: what it carries and how much.
 *
 * For the 3D view, whose labels are floating chips with their own collision
 * avoidance and no room to stack. The 2D drawing takes the two parts
 * separately and stacks them — see `drawConn` — because a schematic puts
 * captions ON the line, where width is what runs out first.
 */
export function streamCaption(bal: Balance, streamId: string): string {
  const rate = streamRateText(bal, streamId);
  if (!rate) return '';
  const name = streamMaterial(bal, streamId);
  return name ? `${name} · ${rate}` : rate;
}
